"use client";

import { Decorative } from "@faire/design-tokens";
import { route as posHomeRoute } from "@faire/web-api/routes/point-of-sale";
import { route as posAboutRoute } from "@faire/web-api/routes/point-of-sale/about";
import { isNotUndefined } from "@faire/web/common/typescriptUtils";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { useViewport } from "@faire/web/ui/hooks/useViewport";
import { removeLocaleFromUrl } from "@faire/web/ui/routing/util";
import compact from "lodash/compact";
import { observer } from "mobx-react";
import React, { useEffect, useMemo } from "react";
import { matchPath, useLocation } from "react-router-dom";
import styled, { css } from "styled-components";

import { AnimatedBanner } from "@faire/retailer/components/AnimatedBanner/AnimatedBanner";
import { AmexIncentiveBanner } from "@faire/retailer/components/Notifications/AmexIncentiveBanner";
import { BrandPreviewBanner } from "@faire/retailer/components/Notifications/BrandPreviewBanner";
import { BrandsCountBanner } from "@faire/retailer/components/Notifications/BrandsCountBanner";
import { FirstOrderIncentiveBanner } from "@faire/retailer/components/Notifications/Components/FirstOrderIncentiveBanner/FirstOrderIncentiveBanner";
import { MarketCountdownBanner } from "@faire/retailer/components/Notifications/Components/MarketCountdownBanner/MarketCountdownBanner";
import { OpeningOrderIncentivesBanner } from "@faire/retailer/components/Notifications/Components/OpeningOrderIncentivesBanner/OpeningOrderIncentivesBanner";
import { ReferralFollowupBanner } from "@faire/retailer/components/Notifications/Components/ReferralFollowupBanner";
import { FaireValuesBanner } from "@faire/retailer/components/Notifications/FaireValuesBanner";
import { EmployeeGrantedFreeFreightBanner } from "@faire/retailer/components/Notifications/FreeFreightBanners";
import { OpenWithFaireEligibleToApplyBanner } from "@faire/retailer/components/Notifications/OpenWithFaireEligibleToApplyBanner";
import { PosIntegrationBanner } from "@faire/retailer/components/Notifications/PosIntegrationBanner";
import { PromoBannerType } from "@faire/retailer/components/Notifications/PromoBanner/bannerOrder";
import { SpecialHolidayNet90PromoBanner } from "@faire/retailer/components/Notifications/SpecialHolidayNet90PromoBanner";
import { WarehouseSaleBanner } from "@faire/retailer/components/Notifications/WarehouseSaleBanner";
import { usePOSSummary } from "@faire/retailer/features/PointOfSale/hooks/usePOSSummary";
import { useIsLoggedInRetailer } from "@faire/retailer/lib/isLoggedInRetailer";
import { useSettingNavSearchUX } from "@faire/retailer/settings/useSettingNavSearchUX";
import { useMarketStore } from "@faire/retailer/stores/domain/Events/FaireMarkets/MarketStore";
import { useRecordMilestoneByDay } from "@faire/retailer/stores/domain/useMilestone";
import { useLoggedOutPromoBanners } from "@faire/retailer/stores/ui/useLoggedOutPromoBanners";
import { usePromoBanners } from "@faire/retailer/stores/ui/usePromoBanners";

import { FaireDirectIncentivesBanner } from "../Components/FaireDirectIncentivesBanner/FaireDirectIncentivesBanner";
import { InsiderAutoEnrollBanner } from "../Components/InsiderAutoEnrollBanner/InsiderAutoEnrollBanner";

import { ConductorBanner } from "./ConductorBanner";

const PromoBannerBase = () => {
  const bannerRef = React.useRef<HTMLDivElement>(null);
  const isLoggedInRetailer = useIsLoggedInRetailer();
  const { isMobile } = useViewport();
  const { bannerHeight, setBannerHeight } = usePromoBanners();

  const { pathname: pathnameWithMaybeLocale } = useLocation();

  const { hasLinkedPOS } = usePOSSummary();

  const pathname = useMemo(
    () => removeLocaleFromUrl(pathnameWithMaybeLocale),
    [pathnameWithMaybeLocale]
  );

  const nonStickyHeader: boolean = useMemo(() => {
    const nonStickyHeaderPath = [
      {
        path: posAboutRoute,
        exact: true,
      },
      ...(!hasLinkedPOS
        ? [
            {
              path: posHomeRoute,
              exact: true,
            },
          ]
        : []),
    ].filter(isNotUndefined);
    return nonStickyHeaderPath.some(
      (path) => matchPath(pathname, path) !== null
    );
  }, [pathname, hasLinkedPOS]);

  const { isShowingCountdownBanner: isShowingMarketCountdownBanner } =
    useMarketStore(["isShowingCountdownBanner"]);
  React.useEffect(() => {
    if (bannerRef.current != null) {
      if (bannerHeight !== bannerRef.current.clientHeight) {
        setBannerHeight(bannerRef.current.clientHeight);
      }
    }
  });

  const recordMilestoneByDay = useRecordMilestoneByDay();
  const { banners } = usePromoBanners();
  useEffect(() => {
    return () => {
      if (banners.includes(PromoBannerType.REFERRAL_FOLLOWUP)) {
        recordMilestoneByDay("HAS_SEEN_REFERRAL_REENGAGEMENT_BANNER");
      }
    };
  }, [banners, recordMilestoneByDay]);

  const loggedOutPromoBanners = useLoggedOutPromoBanners();
  const { isBannerlessVariant } = useSettingNavSearchUX();

  const renderBanners = () => {
    const availableBanners = isLoggedInRetailer
      ? banners
      : loggedOutPromoBanners;

    if (availableBanners.length === 0) {
      return null;
    }

    if (!isLoggedInRetailer && isBannerlessVariant && isMobile) {
      return null;
    }

    // Banners that do not need special styling
    if (
      availableBanners[0] === PromoBannerType.FAIRE_DIRECT_INCENTIVES ||
      availableBanners[0] === PromoBannerType.FAIRE_VALUES ||
      availableBanners[0] === PromoBannerType.MARKET_COUNTDOWN ||
      availableBanners[0] === PromoBannerType.BRAND_PREVIEW
    ) {
      return bannersWithKeyProp();
    }

    return (
      <StyledAnimatedBanner
        banners={bannersWithKeyProp()}
        bannerNames={availableBanners}
      />
    );
  };

  const bannersWithKeyProp = () => {
    return compact(banners.map(getBannerWithKeyProp));
  };

  const getBannerWithKeyProp = (
    type: PromoBannerType,
    index: number
  ): React.ReactNode => {
    const banner = getBanner(type);
    return banner ? (
      <React.Fragment key={index}>{getBanner(type)}</React.Fragment>
    ) : null;
  };

  const getBanner = (type: PromoBannerType): React.ReactNode => {
    switch (type) {
      case PromoBannerType.FAIRE_DIRECT_INCENTIVES:
        return <FaireDirectIncentivesBanner />;
      case PromoBannerType.MARKET_COUNTDOWN:
        return <MarketCountdownBanner />;
      case PromoBannerType.CONDUCTOR_BANNERS:
        return <ConductorBanner />;
      case PromoBannerType.FAIRE_VALUES:
        return <FaireValuesBanner />;
      case PromoBannerType.FIRST_ORDER_INCENTIVE:
        return <FirstOrderIncentiveBanner />;
      case PromoBannerType.INSIDER_AUTO_ENROLL:
        return <InsiderAutoEnrollBanner />;
      case PromoBannerType.SPECIAL_HOLIDAY_NET90:
        return <SpecialHolidayNet90PromoBanner />;
      case PromoBannerType.OPENING_ORDER_INCENTIVES:
        return <OpeningOrderIncentivesBanner />;
      case PromoBannerType.AMEX_INCENTIVE:
        return <AmexIncentiveBanner />;
      case PromoBannerType.BRAND_PREVIEW:
        return <BrandPreviewBanner />;
      case PromoBannerType.BRANDS_COUNTER:
        return <BrandsCountBanner />;
      case PromoBannerType.WAREHOUSE_SALE:
        return <WarehouseSaleBanner />;
      case PromoBannerType.EMPLOYEE_GRANTED_FREE_FREIGHT:
        return <EmployeeGrantedFreeFreightBanner />;
      case PromoBannerType.OPEN_WITH_FAIRE:
        return <OpenWithFaireEligibleToApplyBanner />;
      case PromoBannerType.POS_INTEGRATION:
        return <PosIntegrationBanner />;
      case PromoBannerType.REFERRAL_FOLLOWUP:
        return <ReferralFollowupBanner />;
      default:
        return null;
    }
  };

  return (
    <BannerWrapper
      ref={bannerRef}
      $isSticky={!nonStickyHeader && isShowingMarketCountdownBanner}
    >
      {renderBanners()}
    </BannerWrapper>
  );
};

export const PromoBanner = observer(PromoBannerBase);

const StyledAnimatedBanner = styled(AnimatedBanner)`
  background-color: ${Decorative.tint.neutral};
`;

const BannerWrapper = styled.div<{ $isSticky?: boolean }>`
  z-index: 2;
  position: relative;
  ${({ $isSticky }) =>
    $isSticky
      ? css`
          ${responsiveMediaQueries.desktopAndAbove} {
            position: sticky;
            top: 0;
            z-index: 13;
          }
        `
      : null};
`;
